import { authAdapter } from '@/app/_auth/auth'
import { useAuth } from '@/app/_auth/auth-hooks'
import { useDomainFromUrl } from '@/app/_common/domain/use-domain-from-url'
import { useSplitView } from '@/app/_common/split-view/split-view-context'
import { createStaticTab, createWorkflowTab } from '@/app/_common/split-view/tabs/factories'
import { TAB_TYPE_CHAT, TabTypeStatic, type TabTypeChat } from '@/app/_common/split-view/tabs/types'
import { AgentRegistryIcon } from '@/components/icons/agent-registry-icon'
import { DataCatalogIcon } from '@/components/icons/data-catalog-icon'
import { HistoryIcon } from '@/components/icons/history-icon'
import { PlusIcon } from '@/components/icons/plus-icon'
import { ScheduleIcon } from '@/components/icons/schedule-icon'
import { ServiceRegistryIcon } from '@/components/icons/service-registry-icon'
import { Button } from '@/components/ui/button'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAllWorkflowsQuery } from '@/hooks/api/use-all-workflows-query'
import { RenderTextTruncated } from '@/hooks/use-text-truncation'
import { cn, GA_EVENTS } from '@/lib/utils'
import { T, useTranslate } from '@tolgee/react'
import { cva, VariantProps } from 'class-variance-authority'
import { Bot } from 'lucide-react'
import React, { useCallback } from 'react'
import ReactGA from 'react-ga4'

function SideNavigation() {
  const { t } = useTranslate()
  const refHistoryNavButton = React.useRef<HTMLButtonElement>(null)
  const [isHistoryCardOpen, setIsHistoryCardOpen] = React.useState(false)
  const closeHistoryCard = React.useCallback(() => setIsHistoryCardOpen(false), [])
  const { data: chatsData } = useAllWorkflowsQuery({
    queryOptions: { select: (d) => d.data.slice(0, 5) },
  })
  const { user } = useAuth()
  const { domainId } = useDomainFromUrl()

  const handleGenAgentClick = useCallback(() => {
    // Track analytics
    ReactGA.event({
      action: GA_EVENTS.ACTION.BTN,
      category: GA_EVENTS.CATEGORY.NAV,
      label: 'navigate_gen_agent',
    })

    // Get client_id
    const clientId = authAdapter?.getUserId() ?? user?.uid ?? ''

    // Construct URL with query params
    const url = new URL('https://em-gen-agent-ui.corp.dev.emergence.ai')
    url.searchParams.set('client_id', clientId)
    url.searchParams.set('domain_id', domainId)

    // Open in new tab
    window.open(url.toString(), '_blank')
  }, [user, domainId])

  return (
    <article className='flex h-full max-h-full border-r border-neutral-75 bg-neutral-25'>
      <div className='flex w-[64px] flex-col items-center gap-2 px-3 transition-[width] ease-out'>
        <nav className='my-6 flex flex-col items-center gap-6'>
          <NavButton
            variant='orangeCircle'
            tooltipContent={t('navigation.newChat.title')}
            analyticsLabel='navigate_home'
            tabOptions={{ type: TabTypeStatic.NewChat }}
          >
            <PlusIcon strokeWidth={3} className='size-4 shrink-0 text-neutral-white' />
          </NavButton>
          <HoverCard
            openDelay={0}
            closeDelay={500}
            open={isHistoryCardOpen}
            onOpenChange={setIsHistoryCardOpen}
          >
            <HoverCardTrigger asChild>
              <NavButton
                ref={refHistoryNavButton}
                variant='icon'
                analyticsLabel='navigate_history'
                tabOptions={{ type: TabTypeStatic.History }}
                onAfterNavigate={closeHistoryCard}
                // This is a workaround to prevent the hover card from flashing when clicking on the button
                onPointerDownCapture={() => refHistoryNavButton.current?.focus()}
              >
                <HistoryIcon className='size-8 shrink-0' />
              </NavButton>
            </HoverCardTrigger>
            <HoverCardContent
              align='start'
              side='right'
              sideOffset={24}
              className='w-full min-w-48 max-w-[235px] rounded-lg border border-neutral-50 bg-neutral-25 p-1.5'
            >
              {chatsData?.map((chat) => {
                const title = chat?.title || 'Chat'
                return (
                  <RenderTextTruncated
                    key={chat.workflowId}
                    text={title}
                    className='font-light'
                    render={({ isTruncated, children }) => (
                      <NavButton
                        variant='label'
                        analyticsLabel='navigate_chat'
                        onAfterNavigate={closeHistoryCard}
                        tooltipContent={isTruncated ? title : null}
                        tabOptions={{
                          type: TAB_TYPE_CHAT,
                          workflowId: chat.workflowId,
                          title,
                        }}
                      >
                        {children}
                      </NavButton>
                    )}
                  />
                )
              })}
              <hr className='my-1' />
              <NavButton
                variant='label'
                analyticsLabel='navigate_history'
                tabOptions={{ type: TabTypeStatic.History }}
                onAfterNavigate={closeHistoryCard}
              >
                <HistoryIcon className='shrink-0' />
                <span className='text-sm font-light text-neutral-500'>
                  <T keyName='navigation.history.title' />
                </span>
              </NavButton>
            </HoverCardContent>
          </HoverCard>
          <NavButton
            analyticsLabel='navigate_agent_registry'
            tooltipContent={t('navigation.agentRegistry.title')}
            tabOptions={{ type: TabTypeStatic.AgentRegistry }}
          >
            <AgentRegistryIcon className='shrink-0' />
          </NavButton>
          <NavButton
            analyticsLabel='navigate_service_registry'
            tooltipContent={t('navigation.serviceRegistry.title')}
            tabOptions={{ type: TabTypeStatic.ServiceRegistry }}
          >
            <ServiceRegistryIcon className='shrink-0' />
          </NavButton>
          <NavButton
            tooltipContent={t('navigation.dataCatalog.title')}
            analyticsLabel='navigate_data_governance'
            tabOptions={{ type: TabTypeStatic.DataCatalog }}
          >
            <DataCatalogIcon className='size-8 shrink-0' />
          </NavButton>
          <NavButton
            tooltipContent={t('navigation.schedule.title')}
            analyticsLabel='navigate_schedule'
            tabOptions={{ type: TabTypeStatic.Schedule }}
          >
            <ScheduleIcon className='shrink-0' />
          </NavButton>
          {['dev'].includes(import.meta.env.VITE_ENVIRONMENT) && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  unstyled
                  onClick={handleGenAgentClick}
                  className={cn(navButtonVariants({ variant: 'icon' }))}
                  aria-label='Gen Agent'
                >
                  <Bot className='size-8 shrink-0' strokeWidth={1} />
                </Button>
              </TooltipTrigger>
              <TooltipContent
                side='right'
                align='center'
                sideOffset={24}
                className='h-fit max-w-80 whitespace-pre-line text-pretty'
              >
                Gen Agent
              </TooltipContent>
            </Tooltip>
          )}
        </nav>
      </div>
    </article>
  )
}

// Styling
const navButtonVariants = cva(
  'p-1 h-10 flex items-center whitespace-nowrap gap-2 cursor-pointer select-none rounded text-sm font-normal text-neutral-500 outline-none',
  {
    variants: {
      variant: {
        orangeCircle:
          'size-10 justify-center focus-visible:ring-black/10 focus-visible:ring-2 rounded-full bg-mango-300 transition-all active:scale-[0.98]',
        icon: 'w-10 justify-center hover:bg-neutral-50 focus:bg-neutral-50 [&.active]:bg-neutral-600 [&.active]:text-neutral-white',
        label:
          'gap-3 pl-2 pr-2 hover:bg-neutral-50 focus:bg-neutral-50 [&.active]:bg-neutral-50 [&.active]:focus:bg-neutral-50 w-full',
      },
    },
    defaultVariants: {
      variant: 'icon',
    },
  },
)

type NavButtonVariantProps = VariantProps<typeof navButtonVariants>

type NavButtonTabOptions =
  | {
      type: TabTypeStatic
      title?: string | null
    }
  | {
      type: TabTypeChat
      workflowId: string
      title?: string | null
    }

type NavButtonProps = React.PropsWithChildren<
  {
    className?: string
    analyticsLabel?: string
    tooltipContent?: string | null
    // Tab creation props
    tabOptions: NavButtonTabOptions
    onAfterNavigate?: () => void
  } & NavButtonVariantProps &
    React.HTMLAttributes<HTMLButtonElement>
>

const NavButton = React.forwardRef<HTMLButtonElement, NavButtonProps>(
  (
    {
      variant,
      className,
      children,
      analyticsLabel,
      tabOptions,
      onAfterNavigate,
      tooltipContent,
      ...props
    },
    ref,
  ) => {
    const { addTab, getTabPositionMetadata } = useSplitView()

    const getChatPosition = React.useCallback(
      ({ workflowId }: { workflowId: string }) => {
        return getTabPositionMetadata({ tabType: TAB_TYPE_CHAT, tabId: workflowId })
      },
      [getTabPositionMetadata],
    )

    const handleClick = useCallback(() => {
      if (analyticsLabel) {
        ReactGA.event({
          action: GA_EVENTS.ACTION.BTN,
          category: GA_EVENTS.CATEGORY.NAV,
          label: analyticsLabel,
        })
      }

      const chatPosition =
        tabOptions.type === TAB_TYPE_CHAT
          ? getChatPosition({ workflowId: tabOptions.workflowId })
          : null

      addTab({
        tab: createTabFromOptions(tabOptions),
        panelId: chatPosition?.panelId,
        index: chatPosition?.index,
      })

      onAfterNavigate?.()
    }, [analyticsLabel, tabOptions, onAfterNavigate, addTab, getChatPosition])

    return (
      <NavButtonTooltip variant={variant} tooltipContent={tooltipContent}>
        <Button
          ref={ref}
          unstyled
          onClick={handleClick}
          className={cn(navButtonVariants({ variant }), className)}
          {...(tooltipContent && { 'aria-label': tooltipContent })}
          {...props}
        >
          {children}
        </Button>
      </NavButtonTooltip>
    )
  },
)
NavButton.displayName = 'NavButton'

type NavButtonTooltipProps = Readonly<{
  variant: NavButtonVariantProps['variant']
  tooltipContent?: string | null
  children: React.ReactNode
}>

const NavButtonTooltip: React.FC<NavButtonTooltipProps> = ({
  variant,
  tooltipContent,
  children,
}) => {
  if (!tooltipContent) {
    return children
  }

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side='right'
        align='center'
        sideOffset={variant === 'label' ? 12 : 24}
        className={cn(
          'h-fit whitespace-pre-line text-pretty',
          variant === 'label' ? 'max-w-[260px] text-sm' : 'max-w-80',
        )}
      >
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  )
}

// Helper function to create tabs from options
const createTabFromOptions = (options: NavButtonTabOptions) => {
  if (options.type === TAB_TYPE_CHAT) {
    return createWorkflowTab({
      workflowId: options.workflowId,
      title: options.title,
    })
  }
  return createStaticTab(options.type)
}

export { SideNavigation }
